import { Component, Element, Host, Prop, h } from '@stencil/core';

/**
 *
 * @description A button that contains an icon
 * @use
 * @since 5.10.1
 * @version figma-v1
 */
@Component({
  tag: 'hp6-icon-button',
  styleUrl: 'icon-button.scss',
  shadow: false, // TODO: Determine if shadow DOM will work with this component. No slot, and aria attributes mean maybe it should not use shadow.
})
export class IconButton {
  /**
   * id of the button
   */
  @Prop() buttonId: string;

  /**
   * Name of the icon
   */
  // relect single word prop
  @Prop({ reflect: true }) name: string;

  /**
   * The style of this. Uses hp-themes colors
   */
  // relect css dependency
  @Prop({ reflect: true }) typeSelection: 'primary' | 'secondary' | 'floating' = 'primary';

  /**
   * Invert styles
   */
  @Prop() isInverted: boolean;

  /**
   * Round corners
   */
  @Prop() isRounded: boolean;

  /**
   * Vertical Size of the button
   */
  @Prop({ reflect: true }) sizeSelection: 'sm' | 'md' = 'md'; // reflect CSS dependency

  /**
   * The aria-label attribute of the button tag
   */
  @Prop() allyLabel: string;

  /**
   * the aria-expanded attribute of the button tag
   */
  @Prop() allyExpanded: string;

  /**
   * The aria-controls attribute of the button tag
   */
  @Prop() allyControls: string;

  /**
   * Disabled when true
   */
  @Prop() isDisabled: boolean = false;

  /**
   * Size of the icon
   */
  @Prop() iconSizeSelection: 'xs' | 'sm' | 'md' = 'sm';

  /**
   * Used for accessibility to describe icon
   */
  // relect single word prop

  @Prop({ reflect: true }) description: string = '';

  /**
   * The aria-label of the badge component & also concatenated (at the end) to the button aria-label
   */
  @Prop() badgeAllyLabel: string;

  /**
   * The badge content. Can be either a number or a short string
   */
  @Prop() badgeContent: string;

  /**
   * If true, a border around the badge will appear
   */
  @Prop() badgeShowBorder: boolean;

  /**
   * If true, the badge will be invisible
   */
  @Prop() badgeHide: boolean = false;

  /**
   * If the badge content is a number, the maximum value allowed
   */
  @Prop() badgeMax: number;

  /**
   * Wrapped shape the badge should overlap
   */
  @Prop() badgeOverlapSelection: 'rectangle' | 'circle' = 'rectangle';

  /**
   * Controls whether the badge is hidden when the badgeContent is the number 0
   */
  @Prop() badgeShowZero: boolean;

  /**
   * The dot property changes a badge into a small dot. This can be used as a notification that something has changed without displaying the badge content.
   */
  @Prop() badgeVariantSelection: 'standard' | 'dot' = 'standard';

  @Element() host: HTMLElement;

  /**
   * Used to pass the tabindex of the host to the button
   */
  get tabIndex() {
    return parseInt(this.host.getAttribute('tabindex'), 10) || 0;
  }

  render() {
    const {
      name,
      iconSizeSelection,
      description,
      allyLabel: allyLabelProp,
      allyExpanded,
      allyControls,
      isDisabled,
      buttonId,
      isInverted,
      isRounded,
      tabIndex,
      badgeAllyLabel,
      badgeContent,
      badgeShowBorder,
      badgeHide,
      badgeMax,
      badgeOverlapSelection,
      badgeShowZero,
      badgeVariantSelection,
    } = this;

    let ariaLabelValue = allyLabelProp;

    if (badgeContent || badgeAllyLabel) {
      ariaLabelValue = `${allyLabelProp}, ${badgeContent} ${badgeAllyLabel}`;
    }

    let attributes = {};

    if (allyControls) {
      attributes = {
        ...attributes,
        'aria-controls': allyControls,
      };
    }

    return (
      <Host class={`${isInverted ? 'inverted' : ''} ${isRounded ? 'rounded' : ''}`}>
        <button
          id={buttonId}
          type="button"
          aria-label={ariaLabelValue}
          disabled={isDisabled}
          aria-expanded={allyExpanded}
          tabIndex={tabIndex}
          {...attributes}
        >
          <hp6-badge
            badgeContent={badgeContent}
            showBorder={badgeShowBorder}
            hideBadge={badgeHide}
            max={badgeMax}
            overlapSelection={badgeOverlapSelection}
            showZero={badgeShowZero}
            variantSelection={badgeVariantSelection}
          >
            <hp6-icon name={name} size-selection={iconSizeSelection} description={description} />
          </hp6-badge>
        </button>
      </Host>
    );
  }
}
